import React from "react";
import { Link } from "react-router-dom";
import { Mail, Shield, RotateCcw } from "lucide-react";
import { useCurrency, useAuth } from "../App";
import NotificationsPane from "./NotificationsPane";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const logoSrc = (url) => (url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "/logo-white.png");

/**
 * DesktopShell
 * On desktop (lg+) for the end-user (role: "user"), wraps the app in a 3-column layout:
 *   1. Green segment       – solid emerald-900 brand panel with logo + bottom links
 *   2. App segment         – fixed 480px column containing the customer app
 *   3. Notifications pane  – fills remaining space with the user's notifications feed
 *
 * Below lg, or for unauthenticated users / non-customer roles, children render unchanged.
 */
export default function DesktopShell({ children }) {
  const { homeLogoUrl, logoUrl } = useCurrency();
  const { user } = useAuth();
  const brandLogo = homeLogoUrl || logoUrl;
  const year = new Date().getFullYear();

  if (!user || user.role !== "user") {
    return <>{children}</>;
  }

  return (
    <div className="lg:flex lg:h-screen lg:overflow-hidden dl-shell">
      {/* ===== 1. GREEN SEGMENT ===== */}
      <aside
        data-testid="desktop-shell-left-panel"
        className="hidden lg:flex lg:flex-col lg:w-[360px] xl:w-[400px] lg:h-screen flex-shrink-0 relative overflow-hidden text-white bg-emerald-900"
      >
        {/* Top wordmark */}
        <div className="px-10 pt-10">
          <p className="text-[11px] uppercase tracking-[0.4em] text-emerald-100/80 font-semibold">
            Super Morsel
          </p>
        </div>

        {/* Centered logo (15% smaller than before) */}
        <div className="flex-1 flex items-center justify-center p-10">
          <div className="w-full max-w-[17rem] aspect-square flex items-center justify-center">
            <img
              src={logoSrc(brandLogo)}
              alt="Super Morsel"
              className="w-full h-full object-contain drop-shadow-[0_12px_40px_rgba(0,0,0,0.35)]"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/logo-white.png";
              }}
              data-testid="desktop-shell-logo"
            />
          </div>
        </div>

        {/* Bottom links */}
        <div className="px-10 pb-10">
          <p className="text-[10px] uppercase tracking-[0.35em] text-emerald-100/70 font-semibold mb-4">
            Get in touch
          </p>

          <ul className="space-y-1.5 mb-6">
            <li>
              <a
                href="mailto:support@supermorsel.in"
                className="group flex items-center gap-3 py-2 text-sm text-emerald-50/90 hover:text-white transition-colors"
                data-testid="desktop-shell-customer-care"
              >
                <span className="w-8 h-8 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors">
                  <Mail size={14} />
                </span>
                Customer Care
              </a>
            </li>
            <li>
              <Link
                to="/privacy-policy"
                className="group flex items-center gap-3 py-2 text-sm text-emerald-50/90 hover:text-white transition-colors"
                data-testid="desktop-shell-privacy"
              >
                <span className="w-8 h-8 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors">
                  <Shield size={14} />
                </span>
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link
                to="/refund-policy"
                className="group flex items-center gap-3 py-2 text-sm text-emerald-50/90 hover:text-white transition-colors"
                data-testid="desktop-shell-refund"
              >
                <span className="w-8 h-8 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-colors">
                  <RotateCcw size={14} />
                </span>
                Refund Policy
              </Link>
            </li>
          </ul>

          <div className="pt-4 border-t border-white/15">
            <p className="text-[10px] text-emerald-100/60 tracking-wide">
              © {year} Super Morsel. Live the Superfood Lifestyle.
            </p>
          </div>
        </div>
      </aside>

      {/* ===== 2. APP SEGMENT (flush against green) ===== */}
      <main
        data-testid="desktop-shell-app-pane"
        className="dl-shell-app-pane lg:w-[480px] lg:flex-shrink-0 lg:h-screen lg:overflow-y-auto bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800"
      >
        {children}
      </main>

      {/* ===== 3. NOTIFICATIONS SEGMENT ===== */}
      <aside
        data-testid="desktop-shell-notifications-pane"
        className="hidden lg:flex lg:flex-col lg:flex-1 lg:min-w-0 lg:h-screen lg:overflow-hidden"
      >
        <NotificationsPane />
      </aside>
    </div>
  );
}
